import React, { useState, useEffect, useRef } from "react";
import "./editServiceModal.css";
import { FiClock, FiTag, FiTool, FiX } from "react-icons/fi";
import { IoChevronDownOutline, IoCheckmarkSharp } from "react-icons/io5";
import { GoTrash } from "react-icons/go";
import { motion, AnimatePresence } from "framer-motion";
import DeleteConfirmModal from "./DeleteConfirmModal";
import toast from "react-hot-toast";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getInitialDurationState = (service) => {
  const serviceDuration = toNumber(service?.duration);

  if ([15, 30, 60, 120].includes(serviceDuration)) {
    return {
      duration: serviceDuration,
      customDuration: "",
    };
  }

  return {
    duration: "Other",
    customDuration: serviceDuration ?? "",
  };
};

const getInitialServiceCountState = (service) => {
  const rawServiceCount = service?.service_count;
  const normalizedString = String(rawServiceCount ?? "").trim().toLowerCase();
  const serviceCount = toNumber(rawServiceCount);

  if (
    normalizedString === "unlimited" ||
    rawServiceCount === null ||
    rawServiceCount === undefined ||
    rawServiceCount === ""
  ) {
    return {
      numServices: "Unlimited",
      customNum: "",
    };
  }

  if (["10", "20"].includes(String(serviceCount))) {
    return {
      numServices: String(serviceCount),
      customNum: "",
    };
  }

  if (serviceCount && serviceCount > 0) {
    return {
      numServices: "Other",
      customNum: String(serviceCount),
    };
  }

  return {
    numServices: "Unlimited",
    customNum: "",
  };
};

export default function EditServiceModal({
  onClose,
  service,
  onServiceUpdated,
  onServiceDeleted,
}) {
  const modalRef = useRef(null);

  const initialDurationState = getInitialDurationState(service);
  const initialServiceCountState = getInitialServiceCountState(service);

  const [name, setName] = useState(service?.name || "");
  const [price, setPrice] = useState(
    service?.price !== undefined && service?.price !== null
      ? String(service.price)
      : ""
  );
  const [description, setDescription] = useState(service?.description || "");

  const [duration, setDuration] = useState(initialDurationState.duration);
  const [customDuration, setCustomDuration] = useState(
    initialDurationState.customDuration
  );

  const [numServices, setNumServices] = useState(
    initialServiceCountState.numServices
  );
  const [customNum, setCustomNum] = useState(initialServiceCountState.customNum);

  const [openDropdown, setOpenDropdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const durationOptions = [
    { label: "15 minutes", value: 15 },
    { label: "30 minutes", value: 30 },
    { label: "1 hour", value: 60 },
    { label: "2 hours", value: 120 },
    { label: "Other", value: "Other" },
  ];

  const numOptions = ["10", "20", "Unlimited", "Other"];

  const toggleDropdown = (type) => {
    if (loading) return;
    setOpenDropdown((prev) => (prev === type ? null : type));
  };

  const handleSelect = (type, value) => {
    if (loading) return;

    if (type === "duration") {
      setDuration(value);
      if (value !== "Other") setCustomDuration("");
    }

    if (type === "services") {
      setNumServices(value);
      if (value !== "Other") setCustomNum("");
    }

    setOpenDropdown(null);
  };

  const handleSave = async () => {
    const finalDuration =
      duration === "Other"
        ? parseInt(customDuration, 10) || 30
        : parseInt(duration, 10);

    const finalServiceCount =
      numServices === "Other"
        ? parseInt(customNum, 10) || 1
        : numServices === "Unlimited" || !numServices
          ? "Unlimited"
          : parseInt(numServices, 10);

    if (!name.trim() || !price || !description.trim()) {
      toast.error("Please fill all fields before saving.");
      return;
    }

    if (!finalDuration || finalDuration <= 0) {
      toast.error("Please enter a valid duration.");
      return;
    }

    if (
      numServices === "Other" &&
      (!finalServiceCount || Number(finalServiceCount) <= 0)
    ) {
      toast.error("Please enter a valid number of services.");
      return;
    }

    if (Number(price) <= 0) {
      toast.error("Please enter a valid price.");
      return;
    }

    setLoading(true);

    try {
      const updatedService = {
        id: service.id,
        name: name.trim(),
        description: description.trim(),
        duration: finalDuration,
        price: parseFloat(price),
        service_count: finalServiceCount,
        currency: "USD",
        time_unit: "minutes",
        on_off:
          typeof service?.on_off === "boolean" ? service.on_off : false,
      };

      toast.success("Service updated locally!");

      if (onServiceUpdated) {
        onServiceUpdated(updatedService);
      }

      onClose();
    } catch (error) {
      toast.error(`Failed to update: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteModal(false);
    setLoading(true);

    try {
      toast.success("Service deleted locally!");

      if (onServiceDeleted) {
        onServiceDeleted(service.id);
      }

      onClose();
    } catch (err) {
      toast.error(`Failed to delete: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <AnimatePresence>
      <motion.div
        className="create-service-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
      >
        <motion.div
          ref={modalRef}
          className="create-service-modal"
          initial={{ opacity: 0, scale: 0.8, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.85, y: 30 }}
          transition={{ type: "spring", stiffness: 150, damping: 18 }}
          onSubmit={(e) => e.preventDefault()}
        >
          <div className="create-service-header edit-service-header">
            <h2 className="edit-modal-title">Edit Service</h2>
            <button
              type="button"
              className="edit-service-close-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
            >
              <FiX size={24} className="edit-service-close-icon" />
            </button>
          </div>

          <div className="create-service-form">
            <div className="create-service-input-row">
              <div className="create-service-input-group">
                <label>Service Name</label>
                <input
                  type="text"
                  placeholder="Type Service name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="create-service-input-group create-service-dropdown">
                <label>Duration</label>
                <div
                  className="create-service-dropdown-field"
                  onClick={() => toggleDropdown("duration")}
                >
                  <FiClock className="create-service-icon" />
                  <span className="create-service-dropdown-text">
                    {duration === "Other"
                      ? `${customDuration || "Custom"} min`
                      : `${duration} min`}
                  </span>
                  <IoChevronDownOutline className="create-service-chevron" />
                </div>

                <AnimatePresence>
                  {openDropdown === "duration" && (
                    <motion.div
                      className="create-service-dropdown-menu"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                    >
                      {durationOptions.map((opt) => (
                        <div
                          key={opt.value}
                          className="create-service-dropdown-item"
                          onClick={() => handleSelect("duration", opt.value)}
                        >
                          <span>{opt.label}</span>
                          {duration === opt.value ? (
                            <div className="create-service-success-check">
                              <IoCheckmarkSharp />
                            </div>
                          ) : (
                            <div className="create-service-empty-circle"></div>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {duration === "Other" && (
                  <input
                    type="number"
                    className="create-service-custom-input"
                    placeholder="Enter minutes"
                    value={customDuration}
                    onChange={(e) => setCustomDuration(e.target.value)}
                    min="1"
                  />
                )}
              </div>
            </div>

            <div className="create-service-input-row">
              <div className="create-service-input-group">
                <label>Service Price</label>
                <div className="create-service-input-icon-field">
                  <FiTag className="create-service-icon" />
                  <input
                    type="number"
                    placeholder="30"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="create-service-number-input"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="create-service-input-group create-service-dropdown">
                <label>№ of Services</label>
                <div
                  className="create-service-dropdown-field"
                  onClick={() => toggleDropdown("services")}
                >
                  <FiTool className="create-service-icon" />
                  <span className="create-service-dropdown-text">
                    {numServices === "Other"
                      ? customNum || "Custom"
                      : numServices || "Unlimited"}
                  </span>
                  <IoChevronDownOutline className="create-service-chevron" />
                </div>

                <AnimatePresence>
                  {openDropdown === "services" && (
                    <motion.div
                      className="create-service-dropdown-menu"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                    >
                      {numOptions.map((opt) => (
                        <div
                          key={opt}
                          className="create-service-dropdown-item"
                          onClick={() => handleSelect("services", opt)}
                        >
                          <span>{opt}</span>
                          {(numServices || "Unlimited") === opt ? (
                            <div className="create-service-success-check">
                              <IoCheckmarkSharp />
                            </div>
                          ) : (
                            <div className="create-service-empty-circle"></div>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {numServices === "Other" && (
                  <input
                    type="number"
                    className="create-service-custom-input"
                    placeholder="Enter number"
                    value={customNum}
                    onChange={(e) => setCustomNum(e.target.value)}
                    min="1"
                  />
                )}
              </div>
            </div>

            <div className="create-service-input-group create-service-textarea">
              <label>Service Description</label>
              <textarea
                placeholder="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          <div className="create-service-footer edit-service-footer">
            <button
              className="create-service-btn"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
            <button
              className="delete-service-btn"
              onClick={() => setShowDeleteModal(true)}
              disabled={loading}
            >
              <GoTrash /> Delete Service
            </button>
          </div>

          {showDeleteModal && (
            <DeleteConfirmModal
              onCancel={() => setShowDeleteModal(false)}
              onDelete={handleDelete}
            />
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}